import { useState } from 'react';
import { Building2, Check } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { useTranslation } from 'react-i18next';

export const FORM_DIFFUSION_ID = 'form-diffusion';

function CheckIndicator({ checked }) {
    return (
        <div
            className={cn(
                'shrink-0 h-5 w-5 rounded-full border flex items-center justify-center transition-colors',
                checked ? 'border-secondary bg-secondary text-white' : 'border-border bg-white',
            )}
        >
            {checked && <Check className="h-3 w-3" strokeWidth={3} />}
        </div>
    );
}

export default function FormDiffusion({ companies = [], initialValue = 'all', onSubmit, companyName = '' }) {
    const { t } = useTranslation();
    // selectedIds: empty Set = "tous les comptes", non-empty = sélection spécifique
    const [selectedIds, setSelectedIds] = useState(() => {
        if (initialValue === 'all' || !Array.isArray(initialValue)) return new Set();
        return new Set(initialValue.map(String));
    });

    const isAll = selectedIds.size === 0;

    const handleToggleAll = () => {
        setSelectedIds(new Set());
    };

    const handleToggleCompany = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit?.(isAll ? 'all' : [...selectedIds]);
    };

    return (
        <form id={FORM_DIFFUSION_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
            <p className="text-[13px] text-muted-foreground">
                {t('account:choisissezQuelsComptesOntAccesACetteProcedure')} {companyName}
                &quot;.
            </p>

            <div className="space-y-2">
                {/* Option "Tous les comptes" */}
                <div
                    className={cn(
                        'flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors',
                        isAll ? 'border-secondary' : 'border-border hover:bg-muted',
                    )}
                    onClick={handleToggleAll}
                >
                    <Building2 className="h-5 w-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                    <span className="flex-1 text-sm text-foreground">{t('account:tousLesComptes')}</span>
                    <CheckIndicator checked={isAll} />
                </div>

                {/* Sous-comptes directs */}
                {companies.map((company) => {
                    const id = String(company.uniq_id ?? company.id);
                    const isChecked = selectedIds.has(id);
                    return (
                        <div
                            key={id}
                            className={cn(
                                'flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors',
                                isChecked ? 'border-secondary' : 'border-border hover:bg-muted',
                            )}
                            onClick={() => handleToggleCompany(id)}
                        >
                            <Building2 className="h-5 w-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                            <span className="flex-1 text-sm text-foreground">{company.name}</span>
                            <CheckIndicator checked={isChecked} />
                        </div>
                    );
                })}
            </div>
        </form>
    );
}
